import { useContext, useEffect, useMemo } from 'react';

import { keys, noop, pickBy, isEmpty } from 'lodash';

import { cwaUrl } from '/configuration';
import {
  ArticleMetadata,
  CustomPanelType,
  HandlePresentType,
  PublishStatus,
} from '/types';
import {
  AppShellContext,
  FavoritesContext,
  MetadataContext,
  PublishStatusContext,
  UserAccessRestrictionContext,
} from '/utils/contexts';

import useTranslate from '../../utils/translations';
import { getContentTrackingPropertiesFromMetadata } from '../../utils/useTracking';
import useAccessPanel from './AccessPanel';
import useLongTermPreviewPanel from './LongTermPreviewPanel';
import usePresentPanel from './PresentPanel';
import { useDownloadButtons } from './useDownloadButtons';

const calculateDownloadPath = (
  publishStatus: PublishStatus,
  contentType: string,
  metadata: ArticleMetadata,
): string | undefined => {
  if (publishStatus === 'live') {
    return `${cwaUrl}/download/${contentType}/${metadata.slug}`;
  }
  if (publishStatus === 'preview') {
    return `${cwaUrl}/download/preview/article/${metadata.id}`;
  }
  return undefined;
};

const sidebarProps = {
  product: 'effortless',
};

const useTaskbar = (contentType: string, handlePresent: HandlePresentType) => {
  const { configureSidebar } = useContext(AppShellContext);
  const t = useTranslate();
  const publishStatus = useContext(PublishStatusContext);
  const metadata = useContext(MetadataContext);
  const { accessDetails } = useContext(AppShellContext);
  const mntl = accessDetails?.mntl;
  const accessRestrictions = useContext(UserAccessRestrictionContext);
  const { favoritesPanelProps } = useContext(FavoritesContext);

  const downloadPath = calculateDownloadPath(
    publishStatus,
    contentType,
    metadata,
  );
  const downloads = useDownloadButtons(
    downloadPath,
    publishStatus,
    accessRestrictions.downloads,
  );
  const isLongTermPreview = publishStatus === 'long_term_preview';
  const isMintel = mntl?.org === 'Mintel';

  const builtInPanels = useMemo(
    () => ({
      DownloadPanel: { t, downloads },
      ProjectPanel: {
        username: accessDetails?.sub,
        pathBase: `${cwaUrl}/`,
        auth: 'portalJwt',
        usePolling: true,
      },
      SharePanel: {
        path: `${cwaUrl}/api/share-article`,
        auth: 'portalJwt',
        content: { articleId: metadata.id },
        mixpanelLocation: 'Sidebar',
        mixpanelProperties: {
          ...getContentTrackingPropertiesFromMetadata(metadata),
          Panel: 'Share',
        },
      },
      HelpPanel: {
        path: `${cwaUrl}/api/help-message`,
        auth: 'portalJwt',
      },
      FeedbackPanel: {
        t,
        path: `${cwaUrl}/api/article-feedback`,
        auth: 'portalJwt',
        content: { articleId: metadata.id },
      },
      FavoritesPanel: favoritesPanelProps,
    }),
    [t, metadata, accessDetails?.sub, favoritesPanelProps, downloads],
  );

  const canEdit = !!(
    metadata['editor']['email'] === mntl?.mail ||
    mntl?.flags.includes('cwa-edit-all-articles')
  );

  const panelFlags: Record<string, boolean> = useMemo(
    () => ({
      DownloadPanel:
        !isLongTermPreview && builtInPanels.DownloadPanel.downloads.length > 0,
      ProjectPanel:
        !isLongTermPreview && !!accessRestrictions.downloads?.myPresentation,
      AccessPanel:
        !isLongTermPreview &&
        isMintel &&
        !accessRestrictions.isMasquerading &&
        'custom_access' in metadata,
      SharePanel: !isLongTermPreview && !accessRestrictions.toolsDisabled,
      HelpPanel: !isLongTermPreview && !!mntl?.access.clients?.contacting,
      FeedbackPanel: !isLongTermPreview,
      EditPanel: !isLongTermPreview && canEdit,
      PresentPanel: isMintel && !accessRestrictions.toolsDisabled,
      LongTermPreviewPanel:
        canEdit && (publishStatus === 'preview' || isLongTermPreview),
      FavoritesPanel:
        !accessRestrictions.isLibraryLockdownUser &&
        !accessRestrictions.isAnonymousUser &&
        !isLongTermPreview,
    }),
    [
      builtInPanels.DownloadPanel.downloads.length,
      isLongTermPreview,
      accessRestrictions,
      metadata,
      mntl,
      canEdit,
      publishStatus,
      isMintel,
    ],
  );

  const PresentPanel = usePresentPanel(
    handlePresent,
    accessRestrictions.downloads?.fullReport ? downloadPath : undefined,
  );
  const LongTermPreviewPanel = useLongTermPreviewPanel(
    isLongTermPreview,
    metadata.id,
  );
  const AccessPanel = useAccessPanel(
    `/api/article-access/${publishStatus}/${metadata.id}/`,
  );

  const CustomPanels: Record<string, CustomPanelType> = useMemo(
    () => ({
      AccessPanel,
      EditPanel: {
        closeSidebar: noop,
        iconName: 'edit',
        url: `${metadata['protocol']}://${metadata['hostname']}${metadata['mintel_editor_link']}`,
        title: t('Edit'),
        color: 'secondary',
        width: 0,
        id: 'edit-panel',
      },
      PresentPanel,
      LongTermPreviewPanel,
    }),
    [AccessPanel, PresentPanel, LongTermPreviewPanel, metadata, t],
  );

  const panels = useMemo(
    () => ({
      ...pickBy(builtInPanels, (_, panel) => panelFlags[panel]),
      CustomPanel: keys(CustomPanels)
        .filter((panel) => panelFlags[panel])
        .map((panel) => CustomPanels[panel]),
    }),
    [builtInPanels, panelFlags, CustomPanels],
  );

  useEffect(() => {
    if (!isEmpty(mntl) && !accessRestrictions.isAnonymousUser) {
      configureSidebar(panels, sidebarProps);
    }
  }, [mntl, accessRestrictions.isAnonymousUser, panels, configureSidebar]);
};

export default useTaskbar;
